import React, { useState, useMemo, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import "./UserOrders.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrderDetails } from "../../redux/myOrdersRedux";
import { useHistory } from "react-router-dom";
import { ShoppingCartOutlined, ArrowUpward, ArrowDownward, LocalShipping, Restaurant, Store, LocalHospital, Storefront, LocalMall } from "@material-ui/icons";
import PageLayout from "../PageLayout/PageLayout";
import { userRequest } from "../../api/requestMethods";
import { describeOrderDeliveryTime } from "../../utils/deliverySchedule";
import OrderAgainButton from "../Order/OrderAgainButton";
import GroupBasketResumeCards from "../GroupBasket/GroupBasketResumeCards";

// Pastel status chips (tone carries the meaning; nothing shouts).
const makeStyle = (delivery_status) => {
  if (delivery_status === "DELIVERED") {
    return { background: "#eef7f1", color: "#0a5c40" };
  } else if (delivery_status === "REJECTED") {
    return { background: "#fdeeee", color: "#b03a3a" };
  } else if (
    ["ASSIGNED", "ACCEPTED", "PICKED_UP", "DISPATCHED", "IN_TRANSIT", "ARRIVED"].includes(
      delivery_status
    )
  ) {
    return { background: "#e8f1fb", color: "#1d5b96" };
  } else {
    // PROCESSING / PENDING_3PL / anything pending
    return { background: "#fdf6ec", color: "#8a5a06" };
  }
};

// The vendor id an order actually carries (top-level or first item). May be
// the vendor ENTITY _id OR the owner (Users) id — the backend classifier
// resolves both.
const vendorIdOf = (order) =>
  order?.vendorId || order?.orderItems?.[0]?.vendorId || null;

const TYPE_BADGES = {
  PACKAGE: { icon: LocalShipping, label: "Package", color: "#7b1fa2", bgColor: "#f5eefa" },
  HEALTHCARE: { icon: LocalHospital, label: "Healthcare", color: "#1d5b96", bgColor: "#e8f1fb" },
  RETAIL: { icon: Store, label: "Retail", color: "#0f766e", bgColor: "#e8f6f4" },
  BRAND: { icon: Storefront, label: "Brand", color: "#9a5b06", bgColor: "#fdf3e4" },
  RESTAURANT: { icon: Restaurant, label: "Restaurant", color: "#b26a00", bgColor: "#faf0e0" },
  LUSUKU: { icon: LocalMall, label: "Lusuku", color: "#03660c", bgColor: "#e9f6ee" },
};

// Get order type info (icon, label, color).
// Order documents don't persist a vendor type (legacy orders carry ONLY a
// vendorId per item), so the previous heuristic defaulted everything unknown
// to "Restaurant" — an Eagle Mother retail order rendered as a restaurant
// order. `vendorTypes` is the id→TYPE map from POST /weborders/
// classify_vendor_types (resolves the id against all four vendor collections,
// matching entity _id OR owner id).
const getOrderTypeInfo = (order, vendorTypes = {}) => {
  if (order.isPackageDelivery) return TYPE_BADGES.PACKAGE;
  if (order.isBrandOrder) return TYPE_BADGES.BRAND;

  // Explicit hints some payloads carry.
  if (order.orderType === "healthcare" || order.healthcareId) return TYPE_BADGES.HEALTHCARE;
  if (order.orderType === "retail" || order.retailerWholesalerId) return TYPE_BADGES.RETAIL;

  // Item-level vendorType (newer orders) or the backend-resolved type.
  const itemType = (order.orderItems || [])
    .map((i) => String(i.vendorType || "").toUpperCase())
    .find(Boolean);
  const resolved = itemType || String(vendorTypes[String(vendorIdOf(order))] || "").toUpperCase();

  if (resolved === "RESTAURANT") return TYPE_BADGES.RESTAURANT;
  if (["RETAILER", "WHOLESALER", "ONLINE"].includes(resolved)) return TYPE_BADGES.RETAIL;
  if (resolved === "HEALTHCARE") return TYPE_BADGES.HEALTHCARE;
  if (["BRAND", "BRAND_OWNER"].includes(resolved)) return TYPE_BADGES.BRAND;

  // No vendor-entity match → Lusuku in-house (honest default; the old code
  // mislabelled these — and every unresolved vendor — as "Restaurant").
  return TYPE_BADGES.LUSUKU;
};

const UserOrders = () => {
  const { myOrders, isFetching } = useSelector((state) => state.userOrders);
  const dispatch = useDispatch();
  const history = useHistory();
  
  // Sorting state
  const [sortBy, setSortBy] = useState(null); // 'date', 'status', 'amount'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  // vendorId → vendor TYPE map (RESTAURANT/RETAILER/WHOLESALER/HEALTHCARE/
  // BRAND) for the TYPE badge — resolved once per orders load for the ids
  // that flags/item vendorType can't classify.
  const [vendorTypes, setVendorTypes] = useState({});
  useEffect(() => {
    const unresolved = [
      ...new Set(
        myOrders
          .filter(
            (o) =>
              !o.isPackageDelivery &&
              !o.isBrandOrder &&
              !(o.orderItems || []).some((i) => i.vendorType)
          )
          .map(vendorIdOf)
          .filter(Boolean)
          .map(String)
      ),
    ].filter((id) => vendorTypes[id] === undefined);
    if (unresolved.length === 0) return;
    userRequest
      .post("/weborders/classify_vendor_types", { vendorIds: unresolved })
      .then((r) => {
        const types = r.data?.types || {};
        // Ids the backend couldn't match are marked null so we don't refetch.
        const merged = {};
        unresolved.forEach((id) => {
          merged[id] = types[id] ?? null;
        });
        setVendorTypes((p) => ({ ...p, ...merged }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myOrders]);

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Sort orders based on current sort settings
  const sortedOrders = useMemo(() => {
    if (!sortBy) return myOrders;

    const sorted = [...myOrders].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'date':
          comparison = new Date(a.createdAt) - new Date(b.createdAt);
          break;
        case 'status':
          comparison = a.delivery_status.localeCompare(b.delivery_status);
          break;
        case 'amount':
          comparison = a.totalPrice - b.totalPrice;
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [myOrders, sortBy, sortOrder]);

  // Calculate order statistics
  const totalOrders = myOrders.length;
  const deliveredOrders = myOrders.filter(order => order.delivery_status === "DELIVERED").length;
  const processingOrders = myOrders.filter(order => order.delivery_status === "PROCESSING").length;
  const totalRevenue = myOrders.reduce((sum, order) => sum + (order.totalPrice || 0), 0);

  // Summary component to pass to PageLayout
  const orderSummary = (
    <>
      <div className="summary-badge">
        <div className="summary-badge-label">Total Orders</div>
        <div className="summary-badge-value">{totalOrders}</div>
      </div>
      
      <div className="summary-badge">
        <div className="summary-badge-label">Delivered</div>
        <div className="summary-badge-value" style={{ color: "rgba(71, 236, 52, 0.8)" }}>{deliveredOrders}</div>
      </div>
      
      <div className="summary-badge">
        <div className="summary-badge-label">Processing</div>
        <div className="summary-badge-value" style={{ color: "#FFB84D" }}>{processingOrders}</div>
      </div>
      
      <div className="summary-badge">
        <div className="summary-badge-label">Total Spent</div>
        <div className="summary-badge-value" style={{ fontSize: "1rem" }}>
          {totalRevenue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        </div>
      </div>
    </>
  );

  return (
    <PageLayout
      // icon={LocalShippingOutlined}
      title="Order History"
      subtitle="Track and manage your orders"
      summary={totalOrders > 0 ? orderSummary : null}
    >
      <div className="orders-content">
      {/* An Order together basket under way: a way back into it. */}
      <GroupBasketResumeCards />
      {isFetching ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "47vh",
          }}
        >
          <CircularProgress />
        </div>
      ) : myOrders.length > 0 ? (
          <div className="pbi-table-wrapper">
            <TableContainer component={Paper} className="pbi-table-container">
              <Table sx={{ minWidth: 650 }} aria-label="order history table" stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      { label: "#", sortKey: null, width: 44 },
                      { label: "TYPE", sortKey: null, width: 130 },
                      { label: "ORDER ID", sortKey: null, width: 130 },
                      { label: "CART TOTAL", sortKey: "amount" },
                      { label: "ORDER DATE", sortKey: "date" },
                      { label: "STATUS", sortKey: "status" },
                      /* No header word. "DETAILS" over a cell that also said
                         "Details.." labelled the same thing twice, and an
                         action column is described by its own button. */
                      { label: "", sortKey: null, width: 236 },
                    ].map(({ label, sortKey, width }) => (
                      <TableCell
                        key={label}
                        align="left"
                        onClick={sortKey ? () => handleSort(sortKey) : undefined}
                        className="pbi-th"
                        style={{ width: width || undefined, cursor: sortKey ? "pointer" : "default" }}
                      >
                        <div className="pbi-th-inner">
                          <span>{label}</span>
                          {sortKey && (
                            <span className={`pbi-sort-icon ${sortBy === sortKey ? "active" : ""}`}>
                              {sortBy === sortKey && sortOrder === "asc" ? (
                                <ArrowUpward style={{ fontSize: 13 }} />
                              ) : (
                                <ArrowDownward style={{ fontSize: 13 }} />
                              )}
                            </span>
                          )}
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody className="pbi-tbody">
                  {sortedOrders.map((row, index) => (
                    <TableRow key={row._id} className={`pbi-row ${index % 2 === 0 ? "pbi-row-even" : "pbi-row-odd"}`}>
                      <TableCell className="pbi-td pbi-td-index">{index + 1}.</TableCell>
                      <TableCell className="pbi-td">
                        {(() => {
                          const typeInfo = getOrderTypeInfo(row, vendorTypes);
                          const IconComponent = typeInfo.icon;
                          return (
                            <div className="pbi-type-badge" style={{ background: typeInfo.bgColor }}>
                              <IconComponent style={{ fontSize: 14, color: typeInfo.color }} />
                              <span style={{ color: typeInfo.color }}>{typeInfo.label}</span>
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="pbi-td pbi-td-id">
                        {row._id.slice(-9)}...
                      </TableCell>
                      <TableCell className="pbi-td pbi-td-amount">
                        {row.totalPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      </TableCell>
                      <TableCell className="pbi-td">
                        {row.createdAt.match(/(\d{4}-\d{2}-\d{2})/)[0]}
                        {(() => {
                          const timing = describeOrderDeliveryTime(row);
                          if (!timing) return null;
                          return (
                            <div style={{ marginTop: 4, fontSize: 12, lineHeight: 1.35, color: "#0a5c40" }}>
                              <strong style={{ display: "block", fontWeight: 600 }}>{timing.title}</strong>
                              <span>{timing.detail}</span>
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="pbi-td">
                        <span className="pbi-status" style={makeStyle(row.delivery_status)}>
                          {row.delivery_status.charAt(0).toUpperCase() +
                            row.delivery_status.slice(1).toLowerCase().replace("_", " ")}
                        </span>
                      </TableCell>
                      {/* A real <button>, not a clickable <td>. The cell had
                          an onClick and nothing else: no keyboard focus, no
                          Enter or Space, nothing in the accessibility tree and
                          no focus ring — it worked only because a click fires
                          on anything.

                          "Details.." named the destination in the vaguest
                          possible way and trailed off. "View order" says what
                          the tap does, and it is the one label that is true for
                          every row: Processing, Delivered and Not applicable
                          alike. ("Track order" reads better for the 58 in
                          flight and is wrong for the rest.) */}
                      <TableCell className="pbi-td pbi-td-details">
                        <div className="pbi-row-actions">
                          <button
                            type="button"
                            className="pbi-details-btn"
                            aria-label={`View order ${row._id.slice(-9)}`}
                            onClick={() => {
                              dispatch(fetchOrderDetails(row._id));
                              history.push(`/orderDetail/${row._id}`);
                            }}
                          >
                            View order
                          </button>
                          {/* Order again in one tap: the server rebuilds the
                              order at today's prices and the button puts it
                              in that vendor's cart. */}
                          <OrderAgainButton order={row} surface="account" compact />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
      ) : (
        !isFetching && (
          <div className="emptyStateContainer">
            <div className="emptyStateContent">
              <div className="emptyStateIconWrapper">
                <ShoppingCartOutlined className="emptyStateIcon" />
              </div>
              <h3 className="emptyStateTitle">No Orders Yet</h3>
              <p className="emptyStateDescription">
                You haven't placed any orders yet. Start shopping to see your order history here!
              </p>
              <Button
                variant="contained"
                className="shopNowButton"
                onClick={() => history.push("/")}
                startIcon={<ShoppingCartOutlined />}
              >
                Start Shopping
              </Button>
            </div>
          </div>
        )
      )}
      </div>
    </PageLayout>
  );
};

export default UserOrders;
